import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Money, type AccountOut, type DecimalString } from "@shop-ledger/shared-types";
import { DataTable, type DataTableColumn, usePagination, useSortableData } from "@shop-ledger/ui-components";
import { useAuth } from "../../auth/AuthContext";
import { listAccounts, listLedgerEntries } from "../../api/ledgerApi";
import { StatusBanner } from "../../components/StatusBanner";
import { useAsyncData } from "../../lib/useAsyncData";
import { BranchSelect } from "../../components/BranchSelect";

interface LedgerRow {
  key: string;
  entryId: string;
  description: string;
  referenceSource: string;
  branchId: string | null;
  accountCode: string;
  accountName: string;
  debit: DecimalString;
  credit: DecimalString;
  createdAt: string;
}

/**
 * §5's "real-time General Ledger" — flattens every `LedgerEntryOut.legs[]` into one row
 * per leg (the conventional GL presentation: one line per debit/credit, not one line per
 * multi-leg entry), joined against `GET /ledger/accounts` for the account code/name since
 * `TransactionLegOut` only carries `account_id` (`shared-types/src/api/ledger.ts`).
 *
 * **Date-range filter re-enabled (§10.5, backend fix 2026-07-13)**: `LedgerEntryOut` now
 * carries `created_at` (`api/types.ts`'s local extended type — see its file-level doc
 * comment for why this isn't in `@shop-ledger/shared-types` itself). `GET /ledger/entries`
 * still has no `date_from`/`date_to` query param (only `branch_id`), so this filter runs
 * client-side over the fetched set — consistent with how the account/text filters already
 * work on this page.
 */
export function GeneralLedgerPage() {
  const { ensureFreshToken } = useAuth();
  const [branchId, setBranchId] = useState("");
  const [accountFilter, setAccountFilter] = useState("");
  const [search, setSearch] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const accountsState = useAsyncData(() => ensureFreshToken().then((token) => listAccounts(token)), []);
  const entriesState = useAsyncData(
    () => ensureFreshToken().then((token) => listLedgerEntries(token, branchId || undefined)),
    [branchId],
  );

  const accountById = useMemo(() => {
    const map = new Map<string, AccountOut>();
    for (const account of accountsState.data ?? []) map.set(account.id, account);
    return map;
  }, [accountsState.data]);

  const rows = useMemo<LedgerRow[]>(() => {
    const entries = entriesState.data ?? [];
    const out: LedgerRow[] = [];
    for (const entry of entries) {
      for (const leg of entry.legs) {
        const account = accountById.get(leg.account_id);
        out.push({
          key: leg.id,
          entryId: entry.id,
          description: entry.description ?? "",
          referenceSource: entry.reference_source ?? "",
          branchId: entry.branch_id,
          accountCode: account?.code ?? leg.account_id.slice(0, 8),
          accountName: account?.name ?? "(unknown account)",
          debit: leg.debit,
          credit: leg.credit,
          createdAt: entry.created_at,
        });
      }
    }
    return out;
  }, [entriesState.data, accountById]);

  const filteredByAccount = useMemo(
    () => (accountFilter ? rows.filter((row) => row.accountCode === accountFilter) : rows),
    [rows, accountFilter],
  );

  const filteredByDate = useMemo(() => {
    if (!dateFrom && !dateTo) return filteredByAccount;
    const fromMs = dateFrom ? new Date(`${dateFrom}T00:00:00`).getTime() : -Infinity;
    const toMs = dateTo ? new Date(`${dateTo}T23:59:59`).getTime() : Infinity;
    return filteredByAccount.filter((row) => {
      const ms = new Date(row.createdAt).getTime();
      return ms >= fromMs && ms <= toMs;
    });
  }, [filteredByAccount, dateFrom, dateTo]);

  const searched = useMemo(() => {
    if (!search.trim()) return filteredByDate;
    const needle = search.trim().toLowerCase();
    return filteredByDate.filter(
      (row) =>
        row.description.toLowerCase().includes(needle) ||
        row.referenceSource.toLowerCase().includes(needle) ||
        row.accountName.toLowerCase().includes(needle) ||
        row.accountCode.toLowerCase().includes(needle),
    );
  }, [filteredByDate, search]);

  const { sortedData, sort, requestSort } = useSortableData<LedgerRow, keyof LedgerRow>(searched, (row, key) => {
    const value = row[key];
    return key === "debit" || key === "credit" ? Money.of(value as DecimalString).toNumber() : String(value);
  });
  const { pageData, pagination, setPage } = usePagination(sortedData, 25);

  const totalDebit = Money.sum(searched.map((r) => r.debit));
  const totalCredit = Money.sum(searched.map((r) => r.credit));

  const columns: DataTableColumn<LedgerRow, keyof LedgerRow>[] = [
    {
      key: "createdAt",
      header: "Date",
      sortable: true,
      render: (r) => new Date(r.createdAt).toLocaleDateString(),
    },
    { key: "accountCode", header: "Account", sortable: true, render: (r) => `${r.accountCode} — ${r.accountName}` },
    { key: "description", header: "Description", sortable: true, render: (r) => r.description || "—" },
    { key: "referenceSource", header: "Reference", sortable: true, render: (r) => r.referenceSource || "—" },
    { key: "debit", header: "Debit", sortable: true, align: "right", render: (r) => (Money.isZero(r.debit) ? "—" : r.debit) },
    {
      key: "credit",
      header: "Credit",
      sortable: true,
      align: "right",
      render: (r) => (Money.isZero(r.credit) ? "—" : r.credit),
    },
    {
      key: "entryId",
      header: "Entry",
      render: (r) => <span className="muted">{r.entryId.slice(0, 8)}</span>,
    },
  ];

  return (
    <div className="page">
      <div className="page__header">
        <div>
          <h1 className="page__title">General Ledger</h1>
          <p className="page__subtitle">Every posted transaction leg, one row per debit/credit.</p>
        </div>
        <Link className="sl-button sl-button--primary sl-button--md" to="/ledger/new">
          + New Journal Entry
        </Link>
      </div>

      {entriesState.error ? <StatusBanner kind="error">{entriesState.error}</StatusBanner> : null}
      {accountsState.error ? <StatusBanner kind="error">{accountsState.error}</StatusBanner> : null}

      <div className="filter-bar">
        <BranchSelect id="gl-branch" label="Branch" value={branchId} onChange={setBranchId} allOptionLabel="All branches" />
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="gl-account">
            Account
          </label>
          <select id="gl-account" className="sl-input" value={accountFilter} onChange={(e) => setAccountFilter(e.target.value)}>
            <option value="">All accounts</option>
            {(accountsState.data ?? []).map((account) => (
              <option key={account.id} value={account.code}>
                {account.code} — {account.name}
              </option>
            ))}
          </select>
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="gl-search">
            Search
          </label>
          <input
            id="gl-search"
            className="sl-input"
            placeholder="description or reference"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="gl-date-from">
            From
          </label>
          <input id="gl-date-from" className="sl-input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </div>
        <div className="sl-field">
          <label className="sl-field__label" htmlFor="gl-date-to">
            To
          </label>
          <input id="gl-date-to" className="sl-input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </div>
      </div>
      <p className="muted" style={{ marginTop: -8 }}>
        Date range filtering happens on this page, after loading — there's no server-side date filter for the
        ledger yet, so a very large tenant may see a brief delay while every entry loads first.
      </p>

      <div className="stat-row">
        <div className="stat-tile">
          <p className="stat-tile__label">Rows (filtered)</p>
          <p className="stat-tile__value">{searched.length}</p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Total debit</p>
          <p className="stat-tile__value">{totalDebit}</p>
        </div>
        <div className="stat-tile">
          <p className="stat-tile__label">Total credit</p>
          <p className="stat-tile__value">{totalCredit}</p>
        </div>
      </div>

      <div className="panel">
        {entriesState.loading || accountsState.loading ? (
          <p className="muted">Loading…</p>
        ) : (
          <DataTable
            columns={columns}
            rows={pageData}
            getRowId={(row) => row.key}
            sort={sort}
            onSortChange={requestSort}
            pagination={pagination}
            onPageChange={setPage}
            emptyMessage="No ledger entries match these filters."
            caption="General Ledger"
          />
        )}
      </div>
    </div>
  );
}
